import { format } from 'date-fns/fp';
import { Box, TableCell, TableRow, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';

import { formatCurrency, getCurrencyIcon } from '../../../lib/currencyUtils';
import { AffiliateCreditReason, AffiliateCreditTransactionStatus, Currency } from '../../../lib/enum';
import { AffiliateCreditTransactionItem } from '../../../lib/types';

interface CreditTransactionRowProps {
  transaction: AffiliateCreditTransactionItem;
  currency?: Currency;
}

const CreditTransactionRow = ({ transaction, currency }: CreditTransactionRowProps) => {
  const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.head}`]: {
      color: '#6B7280',
      fontWeight: 400,
      fontSize: '14px'
    },
    [`&.${tableCellClasses.body}`]: {
      border: 'none',
      fontSize: '14px',
      color: '#111827',
      padding: '16px 8px'
    }
  }));

  const transactionDate = format('dd/MM/yyyy HH:mm a')(new Date(transaction.transactionDate));

  const formatAmount = () => {
    if (!currency) return transaction.amount.toString();
    try {
      return formatCurrency(transaction.amount, currency);
    } catch {
      return `${transaction.amount} ${currency}`;
    }
  };

  const renderAmountWithIcon = () => {
    if (!currency || transaction.amount === 0) return transaction.amount.toString();

    const formattedAmount = formatAmount();
    const currencyIcon = getCurrencyIcon(currency, 'small');

    if (currencyIcon && currency === Currency.SAR) {
      const IconComponent = currencyIcon;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconComponent color="currentColor" />
          <span>{formattedAmount.replace('SAR', '').trim()}</span>
        </Box>
      );
    }

    return formattedAmount;
  };

  const formatCreditReason = () => {
    switch (transaction.creditReason) {
      case AffiliateCreditReason.COUPON_USED:
        return 'Coupon Used';
      case AffiliateCreditReason.COUPON_USED_MIGRATION:
        return 'Coupon Used Migration';
      case AffiliateCreditReason.LIFETIME_DISCOUNT:
        return 'Lifetime Discount';
      case AffiliateCreditReason.MANUAL:
        return 'Manual';
      default:
        return transaction.creditReason;
    }
  };

  const getStatusColor = () =>
    transaction.creditStatus === AffiliateCreditTransactionStatus.APPLIED ? '#57AE7F' : '#111827';

  return (
    <TableRow>
      <StyledTableCell>{formatCreditReason()}</StyledTableCell>
      <StyledTableCell>
        <Typography sx={{ color: getStatusColor(), fontWeight: 500 }}>
          {transaction.creditStatus}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>{transactionDate}</StyledTableCell>
      <StyledTableCell>{renderAmountWithIcon()}</StyledTableCell>
    </TableRow>
  );
};

export default CreditTransactionRow;

